import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useDropzone } from 'react-dropzone';
import { Box, CircularProgress, IconButton, Link, styled, TextField } from '@mui/material';
import { Attachment, ChevronLeft, Send } from '@mui/icons-material';
import PreviewList from 'components/common/PreviewList';
import usePasteFile from 'hooks/usePasteFile';
import { useChat } from 'modules/chats/hooks/useChats';
import { MessageItem } from 'modules/chats/common/Message';
import auth from 'recoil/atoms/auth';
import { removeItemAtIndex } from 'utils/common';


type ChatMessagesProps = {
    code: string,
    name: string,
    onClose?: () => void,
}

const StyledChat = styled(Box)(({theme}) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',

    '.title': {
        minHeight: 'auto',
        flexGrow: 0,
        fontSize: {
            sm: '1.5rem',
        },
    },

    '.messages': {
        flexGrow: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',

        'div': {
            marginTop: 'auto',
        }
    },

    '.message': {
        whiteSpace: 'pre-wrap',
        padding: '.25rem .5rem',
        backgroundColor: '#f7f7f7',
        borderRadius: 8,
        position: 'relative',
        overflowAnchor: 'none',

        '.header': {
            display: 'flex',
            justifyContent: 'space-between',
        },

        '.time': {
            fontSize: '.6rem',
            color: 'text.disabled'
        },

        '&:before': {
            content: '""',
            width: 0,
            height: 0,
            top: 0,
            position: 'absolute',
            borderStyle: 'solid',

        },

        '&.their': {
            margin: '0 3rem 1rem 1rem',

            '&:before': {
                left: -8,
                borderWidth: '16px 0 0 16px',
                borderColor: 'whitesmoke transparent transparent transparent',
            },
        },

        '&.mine': {
            margin: '0 1rem 1rem 3rem',
            textAlign: 'right',

            '&:before': {
                right: -8,
                borderWidth: '16px 16px 0 0',
                borderColor: 'whitesmoke transparent transparent transparent',
            }
        },

        '&.m-unread': {
            borderRight: `3px solid ${theme.palette.success.main}`,
        },

        'a': {
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },

    '.divider': {
        textAlign: 'center',
        fontSize: '.75rem',
    },

    '#anchor': {
        overflowAnchor: 'auto',
        height: 1,
    },

    '#push': {
        height: '100%',
        overflowAnchor: 'none',
    },

    '.sender': {
        minHeight: 'auto',
        flexGrow: 0,
    },

    '.input': {
        position: 'relative',
    },
}));

const Chat = ({code, name, onClose}: ChatMessagesProps) => {
    const my = useRecoilValue(auth);
    const {chat, loading, loadMore, send} = useChat(code, name);
    const listRef = useRef<HTMLDivElement>(null);
    const [lastScroll, setLastScroll] = useState([0, 0]);
    const [attachments, setAttachments] = useState<File[]>([]);
    const inputRef = usePasteFile<HTMLTextAreaElement>(setAttachments);

    useEffect(() => {
        listRef.current!.scroll(0, 1);
    }, []);

    // Keep scroll on new message loading
    useEffect(() => {
        if (!listRef.current) return;

        if (loading) {
            const curScrollPos = listRef.current.scrollTop;
            const oldScroll = listRef.current.scrollHeight - listRef.current.clientHeight;
            setLastScroll([curScrollPos, oldScroll]);
        } else {
            const [curScrollPos, oldScroll] = lastScroll;
            const newScroll = listRef.current.scrollHeight - listRef.current.clientHeight;
            listRef.current.scrollTop = curScrollPos + (newScroll - oldScroll);
        }
        // eslint-disable-next-line
    }, [loading]);

    const [sentryRef] = useInfiniteScroll({
        loading,
        hasNextPage: chat.hasMore,
        onLoadMore: loadMore,
        rootMargin: '0px 0px 0px 0px',
    });

    const removeAttachment = (i: number) => setAttachments(old => removeItemAtIndex(old, i));

    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        onDropAccepted: (files: File[]) => setAttachments(old => [...old, ...files]),
        noKeyboard: true,
        noClick: true,
        multiple: true,
    });

    const submit = () => {
        const value = inputRef.current!.value.trim();

        if (value || attachments.length)
            send(value, attachments);

        setAttachments([]);
        inputRef.current!.value = '';
        setTimeout(() => listRef.current!.scrollTop = listRef.current!.scrollHeight, 0);
    };

    const keyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <StyledChat {...getRootProps()}>
            <Box className="title">
                {onClose &&
                    <IconButton
                        onClick={onClose}
                        sx={{
                            mr: 1
                        }}
                    >
                        <ChevronLeft/>
                    </IconButton>
                }
                <ChatTitle code={code} name={name}/>
            </Box>
            <Box className="messages" ref={listRef}>
                <Box>
                    {(loading || chat.hasMore) &&
                        <Box textAlign="center" ref={sentryRef}>
                            <CircularProgress color="inherit" size="1rem"/>
                        </Box>
                    }

                    {chat.messages.map((message, i) => <MessageItem
                        key={message.id}
                        message={message}
                        mine={message.author.id === my.id}
                        prev={chat.messages[i - 1]}
                    />)}

                    {chat.pending && <div id="push"/>}

                    <div id="anchor"/>
                </Box>
            </Box>
            <Box className="sender">
                {attachments.length > 0 &&
                    <PreviewList
                        attachments={attachments}
                        remove={removeAttachment}
                    />
                }

                <Box
                    className="input"
                    sx={{marginBottom: {sm: '24px'}}}
                >
                    <TextField
                        id="content"
                        fullWidth
                        multiline
                        maxRows={5}
                        inputProps={{
                            onKeyDown: keyDown,
                        }}
                        variant="outlined"
                        sx={{backgroundColor: isDragActive ? '#1976d2' : undefined}}
                        inputRef={inputRef}
                    />
                    <input {...getInputProps()} />

                    <IconButton
                        onClick={open}
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 40,
                        }}
                    >
                        <Attachment color="primary"/>
                    </IconButton>

                    <IconButton
                        onClick={submit}
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 0
                        }}
                    >
                        <Send color="primary"/>
                    </IconButton>
                </Box>
            </Box>
        </StyledChat>
    );
};

const ChatTitle = ({code, name}: { code: string, name: string }) => {
    let link = '';

    if (code.startsWith('car'))
        link = `/car/${code.split(':').pop()}`

    if (code.startsWith('voyage'))
        link = `/voyage/${code.split(':').pop()}`

    if (link) {
        const [subject, company] = name.split(' (')
        return (
            <>
                <Link href={link}>{subject}</Link> ({company}
            </>
        )
    }

    return <>{name}</>;
};

export default Chat;
